import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

type FeatureStatus = 'active' | 'not_setup' | 'coming_soon'
type FeatureType = 'medication_reminders' | 'shopping_assistance'

interface FeatureCardProps {
  type: FeatureType
  status: FeatureStatus
  summary?: string
  lovedOneId: string
  className?: string
  isPaused?: boolean
}

/**
 * FeatureCard component displays a feature with its status
 * and navigates to the feature detail page when tapped.
 *
 * Compact design for desktop, touch-friendly for mobile.
 */
export function FeatureCard({
  type,
  status,
  summary,
  lovedOneId,
  className,
  isPaused,
}: FeatureCardProps) {
  const { t } = useTranslation()

  const featureConfig = {
    medication_reminders: {
      icon: '💊',
      title: t('dashboard.medicationReminders'),
      path: `/dashboard/${lovedOneId}/medications`,
    },
    shopping_assistance: {
      icon: '🛒',
      title: t('dashboard.shoppingAssistance'),
      path: `/dashboard/${lovedOneId}/shopping`,
    },
  }

  const config = featureConfig[type]

  const getStatusLabel = () => {
    switch (status) {
      case 'active':
        return t('dashboard.active')
      case 'not_setup':
        return t('dashboard.notSetUp')
      case 'coming_soon':
        return t('dashboard.comingSoon')
    }
  }

  const getStatusColor = () => {
    switch (status) {
      case 'active':
        return '#16a34a' // green
      case 'not_setup':
        return '#9ca3af' // gray
      case 'coming_soon':
        return '#6366f1' // indigo
    }
  }

  return (
    <Link to={config.path} className={`feature-card ${className || ''}`}>
      <div className="feature-card-content">
        <div className="feature-icon">{config.icon}</div>
        <div className="feature-info">
          <div className="feature-title">{config.title}</div>
          <div className="feature-meta">
            <span
              className="feature-status"
              style={{
                backgroundColor: `${getStatusColor()}15`,
                color: getStatusColor(),
              }}
            >
              {getStatusLabel()}
            </span>
            {isPaused && (
              <span
                className="feature-status"
                style={{
                  backgroundColor: '#d9770615',
                  color: '#d97706',
                }}
              >
                ⏸ {t('dashboard.paused')}
              </span>
            )}
            {summary && status === 'active' && <span className="feature-summary">{summary}</span>}
          </div>
        </div>
        <span className="feature-arrow">→</span>
      </div>
      <style>{styles}</style>
    </Link>
  )
}

const styles = `
  .feature-card {
    background-color: #fff;
    border-radius: 12px;
    padding: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    text-decoration: none;
    color: inherit;
    display: block;
    min-height: 44px;
    transition: box-shadow 0.2s, transform 0.1s;
  }

  .feature-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .feature-card:active {
    transform: scale(0.98);
  }

  .feature-card-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .feature-icon {
    font-size: 1.25rem;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f3f4f6;
    border-radius: 8px;
    flex-shrink: 0;
  }

  .feature-info {
    flex: 1;
    min-width: 0;
  }

  .feature-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.2;
  }

  .feature-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.25rem;
    flex-wrap: wrap;
  }

  .feature-status {
    display: inline-block;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 500;
  }

  .feature-summary {
    font-size: 12px;
    color: #6b7280;
  }

  .feature-arrow {
    color: #9ca3af;
    font-size: 1.25rem;
    flex-shrink: 0;
  }
`
